import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { cores } from '../../../styles/styleUtils';

const moeda = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarData = (valor) => {
  if (!valor) return '—';

  // Datas YYYY-MM-DD são mantidas sem conversão de fuso.
  if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [ano, mes, dia] = valor.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';

  return data.toLocaleDateString('pt-BR');
};

const nome = (objeto, fallback = '—') => objeto?.nome || fallback;

const CardResumo = ({ titulo, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      backgroundColor: 'rgba(255,255,255,0.05)',
      borderColor: 'rgba(255,255,255,0.18)',
      color: 'white',
    }}
  >
    <Typography sx={{ fontWeight: 700, mb: 1, color: cores.laranja }}>
      {titulo}
    </Typography>
    {children}
  </Paper>
);

const Linha = ({ rotulo, valor, destaque = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.45 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem' }}>
      {rotulo}
    </Typography>
    <Typography
      sx={{
        color: 'white',
        fontSize: '0.9rem',
        fontWeight: destaque ? 700 : 500,
        textAlign: 'right',
      }}
    >
      {valor}
    </Typography>
  </Box>
);

function SaleSummary({ formData }) {
  const financeiro = Array.isArray(formData.financeiro) ? formData.financeiro : [];
  const totalVenda = Number(formData.valor) || 0;

  const resumoFinanceiro = useMemo(() => {
    const totalParcelasCentavos = financeiro.reduce(
      (acc, item) => acc + Math.round((Number(item?.valor || 0) + Number.EPSILON) * 100),
      0
    );
    const totalVendaCentavos = Math.round((totalVenda + Number.EPSILON) * 100);
    const encargosCentavos = financeiro.reduce(
      (acc, item) => acc + Math.round((Number(item?.encargos || 0) + Number.EPSILON) * 100),
      0
    );
    const liquidoCentavos = financeiro.reduce((acc, item) => {
      const liquido =
        item?.valorLiquido !== undefined && item?.valorLiquido !== null
          ? Number(item.valorLiquido)
          : Number(item?.valor || 0) - Number(item?.encargos || 0);
      return acc + Math.round((liquido + Number.EPSILON) * 100);
    }, 0);

    return {
      totalParcelas: totalParcelasCentavos / 100,
      totalEncargos: encargosCentavos / 100,
      totalLiquido: liquidoCentavos / 100,
      conciliado:
        totalVendaCentavos > 0 &&
        financeiro.length > 0 &&
        totalParcelasCentavos === totalVendaCentavos,
      diferenca: (totalVendaCentavos - totalParcelasCentavos) / 100,
    };
  }, [financeiro, totalVenda]);

  const totalComissaoAbertura = formData.comissaoNaoAplicavel
    ? 0
    : (totalVenda * (Number(formData.comissaoPercentAbertura) || 0)) / 100;

  const mesmoVendedor =
    !!formData.vendedorAbertura &&
    !!formData.vendedorFechamento &&
    (formData.vendedorAbertura?._id
      ? String(formData.vendedorAbertura._id) === String(formData.vendedorFechamento?._id)
      : formData.vendedorAbertura?.nome === formData.vendedorFechamento?.nome);

  const totalComissaoFechamento =
    formData.comissaoNaoAplicavel || mesmoVendedor
      ? 0
      : (totalVenda * (Number(formData.comissaoPercentFechamento) || 0)) / 100;

  const valorIndicacao = Number(formData.valorIndicacao) || 0;

  const despesasAutomaticas = [
    ...(totalComissaoAbertura > 0
      ? [{
          nome: mesmoVendedor ? 'Comissão' : 'Comissão de abertura',
          valor: totalComissaoAbertura,
        }]
      : []),
    ...(totalComissaoFechamento > 0
      ? [{ nome: 'Comissão de fechamento', valor: totalComissaoFechamento }]
      : []),
    ...(valorIndicacao > 0
      ? [{ nome: 'Indicação', valor: valorIndicacao }]
      : []),
    ...(resumoFinanceiro.totalEncargos > 0
      ? [{
          nome:
            formData.formaPagamento?.tipo === 'boleto'
              ? 'Taxas de boleto'
              : 'Taxas/encargos de pagamento',
          valor: resumoFinanceiro.totalEncargos,
        }]
      : []),
  ];

  const totalDespesasIdentificadas = despesasAutomaticas.reduce(
    (acc, item) => acc + Number(item.valor || 0),
    0
  );

  const possuiPropostaOrigem =
    formData.idProposta || formData.idPropostaServico || formData.idPropostaSeguro;

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Alert
        severity={resumoFinanceiro.conciliado ? 'success' : 'warning'}
        sx={{ alignItems: 'center' }}
        action={(
          <Chip
            size="small"
            label={resumoFinanceiro.conciliado ? 'Financeiro conciliado' : 'Revisar financeiro'}
            color={resumoFinanceiro.conciliado ? 'success' : 'warning'}
          />
        )}
      >
        {resumoFinanceiro.conciliado
          ? `As parcelas somam exatamente ${moeda(totalVenda)}.`
          : `As parcelas somam ${moeda(resumoFinanceiro.totalParcelas)} e diferem do total da venda em ${moeda(
              Math.abs(resumoFinanceiro.diferenca)
            )}.`}
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <CardResumo titulo="Cliente e atendimento">
          <Linha rotulo="Cliente" valor={nome(formData.cliente, formData.nomeCliente || '—')} />
          <Linha
            rotulo="Local"
            valor={[formData.cidade, formData.estadoSigla || formData.estado].filter(Boolean).join(' / ') || '—'}
          />
          <Linha rotulo="Unidade" valor={nome(formData.unidade)} />
          <Linha rotulo="Atendimento" valor={formData.formaAtendimento || '—'} />
        </CardResumo>

        <CardResumo titulo="Composição da venda">
          <Linha rotulo="Equipamentos / Kit" valor={moeda(formData.valorKit)} />
          <Linha rotulo="Serviços" valor={moeda(formData.valorServico)} />
          <Linha rotulo="Seguro" valor={moeda(formData.valorSeguro)} />
          <Divider sx={{ my: 0.7, borderColor: 'rgba(255,255,255,0.16)' }} />
          <Linha rotulo="Valor total" valor={moeda(totalVenda)} destaque />
        </CardResumo>

        <CardResumo titulo="Pagamento">
          <Linha rotulo="Forma" valor={nome(formData.formaPagamento)} />
          <Linha rotulo="Parcelas" valor={financeiro.length || Number(formData.quantidadeParcelas) || '—'} />
          {formData.formaPagamento?.nome?.includes('Crédito') && (
            <>
              <Linha rotulo="Operadora" valor={nome(formData.operadora)} />
              {formData.formaPagamento?.nome?.includes('Parcelado') && (
                <Linha
                  rotulo="Antecipação"
                  valor={formData.antecipacaoAutomatica === 'sim' ? 'Sim' : 'Não'}
                />
              )}
              <Linha
                rotulo="Taxa aplicada"
                valor={`${Number(formData.taxaCartaoAplicada || 0).toFixed(2).replace('.', ',')}%`}
              />
            </>
          )}
          <Linha rotulo="Total das parcelas" valor={moeda(resumoFinanceiro.totalParcelas)} />
          {resumoFinanceiro.totalEncargos > 0 && (
            <>
              <Linha rotulo="Encargos" valor={moeda(resumoFinanceiro.totalEncargos)} />
              <Linha rotulo="Valor líquido estimado" valor={moeda(resumoFinanceiro.totalLiquido)} />
            </>
          )}
          {formData.dataRecebimento && (
            <Linha rotulo="Data de recebimento" valor={formatarData(formData.dataRecebimento)} />
          )}
        </CardResumo>

        <CardResumo titulo="Responsáveis e comissões">
          <Linha rotulo="Vendedor abertura" valor={nome(formData.vendedorAbertura)} />
          <Linha rotulo="Vendedor fechamento" valor={nome(formData.vendedorFechamento)} />
          {formData.comissaoNaoAplicavel ? (
            <Linha rotulo="Comissão" valor="Não aplicável" />
          ) : (
            <>
              <Linha
                rotulo={`Abertura (${Number(formData.comissaoPercentAbertura) || 0}%)`}
                valor={moeda(totalComissaoAbertura)}
              />
              <Linha
                rotulo={
                  mesmoVendedor
                    ? 'Fechamento'
                    : `Fechamento (${Number(formData.comissaoPercentFechamento) || 0}%)`
                }
                valor={mesmoVendedor ? 'Mesma pessoa — não duplicada' : moeda(totalComissaoFechamento)}
              />
            </>
          )}
        </CardResumo>
      </Box>

      <CardResumo titulo="Parcelas que serão lançadas em Receitas">
        {financeiro.length > 0 ? (
          <TableContainer
            sx={{
              maxHeight: 260,
              '& .MuiTableCell-head': {
                color: '#D9DDE3',
                fontWeight: 700,
                backgroundColor: '#4A4A4A',
                borderBottom: '1px solid rgba(255,255,255,0.12)',
              },
              '& .MuiTableCell-body': {
                color: '#FFFFFF',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              },
              '& .MuiTableRow-root:hover .MuiTableCell-body': {
                backgroundColor: 'rgba(255,255,255,0.03)',
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Parcela</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell>Forma</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell align="right">Encargos</TableCell>
                  <TableCell align="right">Líquido</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financeiro.map((item, index) => {
                  const valor = Number(item?.valor) || 0;
                  const encargos = Number(item?.encargos) || 0;
                  const liquido =
                    item?.valorLiquido !== undefined && item?.valorLiquido !== null
                      ? Number(item.valorLiquido)
                      : valor - encargos;

                  return (
                    <TableRow key={`${item?.parcela || index + 1}-${item?.data || index}`}>
                      <TableCell>{item?.parcela || index + 1}</TableCell>
                      <TableCell>{formatarData(item?.data)}</TableCell>
                      <TableCell>{nome(item?.formaPagamento, nome(formData.formaPagamento))}</TableCell>
                      <TableCell align="right">{moeda(valor)}</TableCell>
                      <TableCell align="right">{moeda(encargos)}</TableCell>
                      <TableCell align="right">{moeda(liquido)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            Nenhuma parcela financeira foi gerada. Retorne à etapa Pagamento.
          </Typography>
        )}
      </CardResumo>

      <CardResumo titulo="Despesas automáticas previstas">
        {despesasAutomaticas.length > 0 ? (
          <>
            {despesasAutomaticas.map((item) => (
              <Linha key={item.nome} rotulo={item.nome} valor={moeda(item.valor)} />
            ))}
            <Divider sx={{ my: 0.7, borderColor: 'rgba(255,255,255,0.16)' }} />
            <Linha
              rotulo="Total identificado neste resumo"
              valor={moeda(totalDespesasIdentificadas)}
              destaque
            />
          </>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            Nenhuma comissão, indicação ou taxa automática foi identificada.
          </Typography>
        )}

        {possuiPropostaOrigem && (
          <Typography
            sx={{
              mt: 1.2,
              color: 'rgba(255,255,255,0.62)',
              fontSize: '0.78rem',
              lineHeight: 1.4,
            }}
          >
            Valores de Projeto/Engenharia e Montagem já registrados nas propostas de origem
            também podem gerar despesas vinculadas à obra no momento do lançamento. Eles não
            entram no total acima porque são lidos diretamente da proposta pelo backend.
          </Typography>
        )}
      </CardResumo>

      <Alert severity="info">
        Ao confirmar, Venda, Receitas, Obra, Despesas automáticas e marcação das propostas
        serão gravadas pela mesma operação transacional. Se alguma etapa falhar, o lançamento
        não deve permanecer parcialmente gravado.
      </Alert>
    </Box>
  );
}

export default SaleSummary;
